const SwatchSwitcherElement = class extends HTMLElement {
  static maxSectionsPerUrl = 5;

  constructor() {
    super();

    window.swatchSwitcher = window.swatchSwitcher || {};
    window.swatchSwitcher.sectionsEnabled = window.swatchSwitcher.sectionsEnabled !== false;

    this.swappableSections = Array.from(document.querySelectorAll('#MainContent > .shopify-section')).map((section) => ({
      sectionId: section.id.replace('shopify-section-', ''),
      selector: '#' + section.id
    }));

    this.swatchLinkTriggerSelector = '[data-swatch]';
    this.allSwatchEls = this.querySelectorAll(this.swatchLinkTriggerSelector);
    this.allVariantIds = [...this.allSwatchEls].map(({ dataset }) => dataset.variantId);

    if (!window.swatchSwitcher.allVariantsAndSections) {
      window.swatchSwitcher.allVariantsAndSections = {};
    }

    [...this.allSwatchEls].forEach((el) => {
      const variantId = parseInt(el.dataset.variantId, 10);
      if (!variantId || window.swatchSwitcher.allVariantsAndSections[variantId]) return;
      let productJson = null;
      try {
        productJson = JSON.parse(el.dataset.swatchProductJson);
      } catch (err) {
        productJson = null;
      }
      window.swatchSwitcher.allVariantsAndSections[variantId] = {
        fetchUrls: this.buildFetchUrls(el.href),
        productJson: productJson,
        sections: this.swappableSections.map((section) => Object.assign({}, section)),
        url: el.href,
        variantId: variantId
      };
    });

    this.allVariantsAndSections = window.swatchSwitcher.allVariantsAndSections;

    this.querySelectorAll('[data-swatch]').forEach((swatch) => {
      swatch.addEventListener('click', this.onSwatchClick.bind(this));
    });

    document.addEventListener('variant:change', this.checkSwatchAvailability.bind(this));

    this.prefetchIdle();
  }

  buildFetchUrls(href) {
    const urls = [];
    for (let i = 0; i < this.swappableSections.length; i += this.constructor.maxSectionsPerUrl) {
      const fetchBase = new URL(href, window.location.origin);
      fetchBase.searchParams.set(
        'sections',
        this.swappableSections.slice(i, i + this.constructor.maxSectionsPerUrl).map(({ sectionId }) => sectionId).join()
      );
      urls.push(fetchBase.toString());
    }
    return urls;
  }

  prefetchIdle() {
    if (window.swatchSwitcher.prefetchStarted) return;
    window.swatchSwitcher.prefetchStarted = true;
    const load = () => {
      const ids = Object.keys(window.swatchSwitcher.allVariantsAndSections);
      Promise.all(ids.map((id) => this.ensureVariantSections(parseInt(id, 10)).catch(() => {}))).then(() => {
        window.swatchSwitcher.sectionsLoaded = true;
      });
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(load, { timeout: 2500 });
    } else {
      setTimeout(load, 400);
    }
  }

  ensureVariantSections(variantId) {
    const entry = window.swatchSwitcher.allVariantsAndSections[variantId];
    if (!entry) return Promise.reject(new Error('Unknown family swatch'));
    if (entry.sections && entry.sections.every((section) => section.contentHtml)) {
      return Promise.resolve(entry);
    }
    if (entry.loadingPromise) return entry.loadingPromise;

    entry.loadingPromise = Promise.all(
      entry.fetchUrls.map((url) => fetch(url).then((response) => {
        if (!response.ok) throw new Error('Family swatch section request failed');
        return window.swatchSwitcher.sectionsEnabled ? response.json() : response.text();
      }))
    ).then((responseArray) => {
      const allSectionsAndHtml = responseArray.reduce((accumulator, value) => Object.assign(accumulator, value), {});
      entry.sections = this.swappableSections.map((section) => ({
        ...section,
        contentHtml: new DOMParser().parseFromString(
          window.swatchSwitcher.sectionsEnabled ? (allSectionsAndHtml[section.sectionId] || '') : allSectionsAndHtml,
          'text/html'
        )
      }));
      return entry;
    }).catch((err) => {
      entry.loadingPromise = null;
      throw err;
    });

    return entry.loadingPromise;
  }

  onSwatchClick(e) {
    e.preventDefault();
    const productEl = e.target.closest(this.swatchLinkTriggerSelector);
    if (!productEl || productEl.classList.contains('active') || this._switching) return;

    const variantId = parseInt(productEl.dataset.variantId, 10);
    this._switching = true;
    this.classList.add('is-loading');

    this.ensureVariantSections(variantId)
      .then(() => this.switchToSwatch(productEl, variantId))
      .catch(() => {
        window.location.href = productEl.href;
      })
      .finally(() => {
        this._switching = false;
        this.classList.remove('is-loading');
      });
  }

  /**
   * Live order: replace HTML first, then unload/load.
   * Dispatch on the section node (not document) so Impulse binds the matching Product.
   */
  reInitSection(target, sectionId) {
    target.dispatchEvent(new CustomEvent('shopify:section:unload', {
      bubbles: true,
      detail: { sectionId }
    }));
    target.dispatchEvent(new CustomEvent('shopify:section:load', {
      bubbles: true,
      detail: { sectionId }
    }));
  }

  resolveSectionSource(contentHtml, selector) {
    if (!contentHtml) return null;
    let source = contentHtml.querySelector(selector);
    if (source) return source;
    // Section Rendering API occasionally returns inner markup without the wrapper.
    const bodyChild = contentHtml.body && contentHtml.body.firstElementChild;
    return bodyChild || null;
  }

  switchToSwatch(productEl, variantId) {
    const entry = window.swatchSwitcher.allVariantsAndSections[variantId];
    if (!entry || !entry.sections) {
      window.location.href = productEl.href;
      return;
    }

    const productData = entry.productJson;
    document.dispatchEvent(new CustomEvent('swatchSwitcher:beforeSwitch', {
      bubbles: true,
      detail: { productData }
    }));

    const updates = [];
    entry.sections.forEach(({ selector, contentHtml, sectionId }) => {
      const target = document.querySelector(selector);
      const source = this.resolveSectionSource(contentHtml, selector);
      if (!target || !source) return;
      updates.push({ target, source, sectionId });
    });

    if (!updates.length) {
      window.location.href = productEl.href;
      return;
    }

    updates.forEach(({ target, source, sectionId }) => {
      target.innerHTML = source.innerHTML;
      this.reInitSection(target, sectionId);
    });

    const nextUrl = new URL(productEl.href, window.location.origin);
    if (variantId) nextUrl.searchParams.set('variant', String(variantId));
    if (history.replaceState && !(window.Shopify && window.Shopify.designMode)) {
      history.replaceState({ path: nextUrl.href }, '', nextUrl.pathname + nextUrl.search + nextUrl.hash);
    }
    document.dispatchEvent(new CustomEvent('swatchSwitcher:switched', {
      bubbles: true,
      detail: { productData }
    }));
  }

  checkSwatchAvailability(event) {
    const newVariant = event.detail && event.detail.variant;
    if (!newVariant || parseInt(this.dataset.position, 10) <= 1) return;
    this.allSwatchEls.forEach((swatch) => {
      try {
        const swatchProductJson = JSON.parse(swatch.dataset.swatchProductJson);
        const availableVariantOptions = swatchProductJson.variants
          .filter((variant) => variant.available && variant.inventory_quantity > 0)
          .map((variant) => JSON.stringify(variant.options));
        swatch.dataset.swatchAvailable = availableVariantOptions.includes(JSON.stringify(newVariant.options)) ? 'true' : 'false';
      } catch (err) {
        return;
      }
    });
  }
};

if (!customElements.get('swatch-switcher')) {
  customElements.define('swatch-switcher', SwatchSwitcherElement);
}
if (!customElements.get('swatch-links')) {
  customElements.define('swatch-links', class extends SwatchSwitcherElement {});
}
